import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Hook to set SEO meta tags, canonical URL, and schema markup for blog pages
 * Usage: useSEO({ blog object from your data })
 */

export const useSEO = (blog) => {
  useEffect(() => {
    if (blog) {
      // Set meta tags in document head
      const metaTags = {
        description: blog.metaDescription || blog.description,
        keywords: blog.metaKeywords || '',
      };

      // Update or create meta tags
      updateMetaTag('description', metaTags.description);
      updateMetaTag('keywords', metaTags.keywords);
      
      // Open Graph Tags
      updateMetaTag('og:title', blog.ogTitle || blog.metaTitle || blog.title, 'property');
      updateMetaTag('og:description', blog.ogDescription || blog.metaDescription || blog.description, 'property');
      updateMetaTag('og:image', blog.ogImage || '', 'property');
      updateMetaTag('og:type', 'article', 'property');
      updateMetaTag('og:url', blog.canonicalUrl || '', 'property');

      // Twitter Card Tags
      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:title', blog.ogTitle || blog.metaTitle || blog.title);
      updateMetaTag('twitter:description', blog.ogDescription || blog.metaDescription || blog.description);
      updateMetaTag('twitter:image', blog.ogImage || '');

      // Canonical URL
      if (blog.canonicalUrl) {
        updateCanonicalLink(blog.canonicalUrl);
      }

      // Schema.org JSON-LD
      if (blog.schemaType) {
        addSchemaMarkup(blog);
      }

      // Update page title
      document.title = blog.metaTitle || blog.title;
    }
  }, [blog]);
};

/**
 * Helper function to update or create meta tags
 */
const updateMetaTag = (name, content, attribute = 'name') => {
  if (!content) return;

  let tag = document.querySelector(`meta[${attribute}="${name}"]`);
  
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  
  tag.setAttribute('content', content);
};

/**
 * Helper function to update or create canonical link
 */
const updateCanonicalLink = (url) => {
  let link = document.querySelector('link[rel="canonical"]');
  
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  
  link.href = url;
};

/**
 * Helper function to add Schema.org JSON-LD markup
 */
const addSchemaMarkup = (blog) => {
  // Remove existing schema if present
  let existingSchema = document.querySelector('script[type="application/ld+json"]');
  if (existingSchema) {
    existingSchema.remove();
  }

  // Create new schema
  const schemaScript = document.createElement('script');
  schemaScript.type = 'application/ld+json';

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': blog.schemaType || 'BlogPosting',
    headline: blog.metaTitle || blog.title,
    description: blog.metaDescription || blog.description,
    image: blog.ogImage || `https://picsum.photos/1200/630?random=${blog.id}`,
    datePublished: blog.datePublished || new Date().toISOString(),
    dateModified: new Date().toISOString(),
    author: {
      '@type': 'Person',
      name: blog.schemaAuthor || 'Jungle Morals',
    },
    publisher: {
      '@type': 'Organization',
      name: blog.schemaPublisher || 'Jungle Morals',
      logo: {
        '@type': 'ImageObject',
        url: 'https://yoursite.com/logo.png', // Update with your logo URL
        width: 250,
        height: 60,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': blog.canonicalUrl || `https://yoursite.com/blog/${blog.slug}`,
    },
  };

  schemaScript.textContent = JSON.stringify(schemaData);
  document.head.appendChild(schemaScript);
};

/**
 * Component wrapper for using Helmet (if you prefer)
 * Usage: <SEOHelmet blog={blog} />
 */
export const SEOHelmet = ({ blog }) => {
  if (!blog) return null;

  return (
    <Helmet>
      <title>{blog.metaTitle || blog.title}</title>
      <meta name="description" content={blog.metaDescription || blog.description} />
      <meta name="keywords" content={blog.metaKeywords || ''} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={blog.canonicalUrl || ''} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={blog.ogTitle || blog.metaTitle || blog.title} />
      <meta property="og:description" content={blog.ogDescription || blog.metaDescription || blog.description} />
      <meta property="og:image" content={blog.ogImage || ''} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={blog.canonicalUrl || ''} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={blog.ogTitle || blog.metaTitle || blog.title} />
      <meta name="twitter:description" content={blog.ogDescription || blog.metaDescription || blog.description} />
      <meta name="twitter:image" content={blog.ogImage || ''} />
      
      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': blog.schemaType || 'BlogPosting',
          headline: blog.metaTitle || blog.title,
          description: blog.metaDescription || blog.description,
          image: blog.ogImage || `https://picsum.photos/1200/630?random=${blog.id}`,
          datePublished: blog.datePublished || new Date().toISOString(),
          dateModified: new Date().toISOString(),
          author: {
            '@type': 'Person',
            name: blog.schemaAuthor || 'Jungle Morals',
          },
          publisher: {
            '@type': 'Organization',
            name: blog.schemaPublisher || 'Jungle Morals',
            logo: {
              '@type': 'ImageObject',
              url: 'https://yoursite.com/logo.png',
              width: 250,
              height: 60,
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': blog.canonicalUrl || `https://yoursite.com/blog/${blog.slug}`,
          },
        })}
      </script>
    </Helmet>
  );
};

export default useSEO;